import type { PrintOrderPayload, PrintResult } from '@/shared/types/electron';
import type { PrinterMode, NetworkPrinterConfig } from '@/features/printer/store';

export interface PrintRouteConfig {
  mode: PrinterMode;
  network: NetworkPrinterConfig | null;
  osPrinterName: string | null;
  paperWidthMm: 58 | 80;
}

/**
 * Single source of truth for "print this order" — used by both auto-print
 * (F-7) and the manual Reprint buttons. Honors the saved printer config:
 *
 *  - `mode: 'network'` + saved host/port → ESC/POS over TCP
 *  - `mode: 'auto'` (or fallback) → ESC/POS via `lp -o raw` to either the
 *    explicitly-picked OS printer or the system default
 *
 * Returns `{ ok: false, error }` when the configuration is incomplete (e.g.
 * network mode without a saved host) so callers can surface a meaningful
 * toast instead of silently failing.
 */
export async function printOrderWithSavedConfig(
  payload: PrintOrderPayload,
  config: PrintRouteConfig,
): Promise<PrintResult> {
  if (typeof window === 'undefined' || !window.electron) {
    return { ok: false, error: 'Not running in the desktop app.' };
  }

  if (config.mode === 'network') {
    if (!config.network) {
      return { ok: false, error: 'Network printer not configured.' };
    }
    return window.electron.printer.printOrder({
      host: config.network.host,
      port: config.network.port,
      paperWidthMm: config.paperWidthMm,
      payload,
    });
  }

  // Auto / OS mode
  const printers = await window.electron.printer.listPrinters();
  if (printers.length === 0) {
    return { ok: false, error: 'No OS-installed printer found.' };
  }
  const picked = config.osPrinterName
    ? printers.find((p) => p.name === config.osPrinterName)
    : null;
  const chosen = picked ?? printers.find((p) => p.isDefault) ?? printers[0];
  return window.electron.printer.printOrderOs({
    deviceName: chosen.name,
    paperWidthMm: config.paperWidthMm,
    payload,
  });
}
